import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  fetchMemberPointsLeaderboard,
  fetchPublicMemberPointsLeaderboard,
  type PublicMemberPointsLeaderboardItem
} from "@/features/public/api";
import { restoreMemberAccessToken, subscribeToMemberSessionChanges } from "@/features/public/memberSession";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

const PODIUM_ORDER = [1, 0, 2] as const;

function formatNumber(value: number) {
  return new Intl.NumberFormat("fr-FR").format(value);
}

function formatShortSteps(value: number) {
  if (value >= 1000) {
    return `${(value / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })}k pas`;
  }

  return `${formatNumber(value)} pas`;
}

function getInitials(member: PublicMemberPointsLeaderboardItem) {
  const source = member.name || `${member.prenom} ${member.nom}`;
  return source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "AS";
}

function Avatar({
  member,
  className
}: {
  member: PublicMemberPointsLeaderboardItem;
  className: string;
}) {
  if (member.avatar) {
    return (
      <img
        src={member.avatar}
        alt={member.name}
        className={`${className} rounded-full object-cover object-[center_24%]`}
      />
    );
  }

  return (
    <div className={`${className} rounded-full bg-[#1A8C7A] text-white flex items-center justify-center font-black`}>
      {getInitials(member)}
    </div>
  );
}

function PodiumMember({
  member
}: {
  member: PublicMemberPointsLeaderboardItem;
}) {
  const isWinner = member.position === 1;
  const orderClass = member.position === 1 ? "order-1 sm:order-2" : member.position === 2 ? "order-2 sm:order-1" : "order-3";

  return (
    <div
      className={`rounded-2xl bg-white/85 border border-white/90 p-4 flex items-center justify-between sm:flex-col sm:justify-start sm:text-center ${orderClass}`}
    >
      <div className={`relative mb-0 sm:mb-3 ${isWinner ? "sm:scale-110" : ""}`}>
        <Avatar
          member={member}
          className={`${isWinner ? "w-16 h-16 sm:w-20 sm:h-20 border-4 border-emerald-500" : "w-14 h-14 sm:w-16 sm:h-16 border-4 border-gray-200"}`}
        />
        <span className="absolute -bottom-1 -right-1 h-7 min-w-7 px-2 rounded-full bg-orange-500 text-white text-xs font-black flex items-center justify-center shadow-lg">
          #{member.position}
        </span>
      </div>
      <div className="flex-1 min-w-0 px-4 sm:px-0">
        <p className="font-bold text-gray-900 text-sm sm:text-base truncate">{member.name}</p>
        <p className="text-[#1A8C7A] font-bold text-lg">{formatNumber(member.points)} pts</p>
        <p className="text-xs text-orange-600 font-semibold">{member.rank}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs sm:text-sm text-gray-500 sm:justify-center">
          <span><i className="ri-footprint-line mr-1"></i>{formatShortSteps(member.totalSteps)}</span>
          <span><i className="ri-calendar-check-line mr-1"></i>{member.counters.eventsAttended} év.</span>
          <span><i className="ri-trophy-line mr-1"></i>{member.counters.challengesDone} défis</span>
        </div>
      </div>
    </div>
  );
}

function LeaderboardRow({ member }: { member: PublicMemberPointsLeaderboardItem }) {
  return (
    <div className={`flex items-start sm:items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 rounded-xl sm:rounded-none border transition-colors ${
      member.isCurrentUser
        ? "border-orange-200 bg-orange-50/80 sm:border-y sm:border-x-0 sm:bg-orange-50/70"
        : "border-gray-100 sm:border-0 bg-white sm:bg-transparent hover:bg-white"
    }`}>
      <span className={`w-8 pt-1 sm:pt-0 text-center font-bold text-sm ${member.isCurrentUser ? "text-orange-600" : "text-gray-400"}`}>#{member.position}</span>
      <Avatar member={member} className="w-10 h-10 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm truncate">{member.isCurrentUser ? "Moi" : member.name}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-400 mt-0.5">
          <span><i className="ri-footprint-line mr-1"></i>{formatShortSteps(member.totalSteps)}</span>
          <span><i className="ri-calendar-check-line mr-1"></i>{member.counters.eventsAttended} événements</span>
          <span><i className="ri-group-line mr-1"></i>{member.counters.squadsJoined + member.counters.teamsJoined} groupes</span>
        </div>
      </div>
      <div className="text-right">
        <span className={`font-bold text-sm sm:text-base whitespace-nowrap ${member.isCurrentUser ? "text-orange-600" : "text-[#1A8C7A]"}`}>{formatNumber(member.points)} pts</span>
        <p className="text-xs text-gray-400">{member.rank}</p>
      </div>
    </div>
  );
}

export default function FitnessTracker() {
  const [leaderboard, setLeaderboard] = useState<PublicMemberPointsLeaderboardItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const loadLeaderboard = useCallback(async () => {
    try {
      const accessToken = await restoreMemberAccessToken().catch(() => null);
      const data = accessToken
        ? await fetchMemberPointsLeaderboard(accessToken)
        : await fetchPublicMemberPointsLeaderboard();
      setLeaderboard(data);
      setError(null);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Classement indisponible.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLeaderboard();
  }, [loadLeaderboard]);

  useEffect(() => subscribeToMemberSessionChanges(() => {
    void loadLeaderboard();
  }), [loadLeaderboard]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void loadLeaderboard();
    }, 300_000);

    return () => window.clearInterval(intervalId);
  }, [loadLeaderboard]);

  useRealtimeInvalidation({
    topics: ["leaderboard"],
    onInvalidate: loadLeaderboard
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const podium = useMemo(
    () => PODIUM_ORDER.map((index) => leaderboard[index]).filter(Boolean) as PublicMemberPointsLeaderboardItem[],
    [leaderboard]
  );
  const rest = leaderboard.slice(3, 10);
  const currentMemberOutsideTopTen = leaderboard.find((member) => member.isCurrentUser && member.position > 10) ?? null;
  const totalSteps = leaderboard.reduce((total, member) => total + member.totalSteps, 0);

  return (
    <section ref={ref} className="py-16 sm:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-10 gap-4 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 text-[#1A8C7A] text-sm font-medium mb-3">
              Classement temps réel
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A]">Classement Sportif</h2>
            <p className="text-gray-500 mt-2 text-sm">Les points ASC sont calculés avec les pas et les actions des membres.</p>
          </div>
          <div className="w-full sm:w-auto grid grid-cols-2 gap-3">
            <div className="bg-teal-50 px-4 sm:px-5 py-3 rounded-2xl">
              <p className="text-sm text-gray-500">Membres classés</p>
              <p className="text-2xl font-bold text-[#1A8C7A]">{formatNumber(leaderboard.length)}</p>
            </div>
            <div className="bg-orange-50 px-4 sm:px-5 py-3 rounded-2xl">
              <p className="text-sm text-gray-500">Pas cumulés</p>
              <p className="text-2xl font-bold text-orange-600">{formatShortSteps(totalSteps)}</p>
            </div>
          </div>
        </div>

        <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                <i className="ri-loader-4-line animate-spin text-3xl text-[#1A8C7A]"></i>
                <p className="mt-3 font-medium">Chargement du classement...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center text-gray-500">
                <i className="ri-wifi-off-line text-3xl text-orange-600"></i>
                <p className="mt-3 font-medium">{error}</p>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <i className="ri-footprint-line text-3xl text-[#1A8C7A]"></i>
                <p className="mt-3 font-medium">Le classement apparaîtra dès que les membres synchroniseront leurs pas.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-br from-[#1A8C7A]/5 to-teal-50">
                  {podium.map((member) => (
                    <PodiumMember key={member.userId} member={member} />
                  ))}
                </div>

                {rest.length > 0 && (
                  <div className="p-3 sm:p-0 sm:divide-y sm:divide-gray-100 space-y-3 sm:space-y-0">
                    {rest.map((member) => (
                      <LeaderboardRow key={member.userId} member={member} />
                    ))}
                    {currentMemberOutsideTopTen && (
                      <>
                        <div className="px-4 sm:px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-orange-600 bg-orange-50/70">
                          Votre position
                        </div>
                        <LeaderboardRow member={currentMemberOutsideTopTen} />
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
